// src/contexts/EmbedRuntimeContext.jsx
// -----------------------------------------------------------------------------
// Enterprise public embed runtime context
//
// PURPOSE
//  • Provide one central place for iframe/embed behavior
//  • Keep iframe-specific behavior OUT of the builder and normal public route
//  • Support HubSpot-style external form embedding patterns:
//      - platform-owned form runtime
//      - parent page owns analytics/listeners
//      - embedded form emits lifecycle events
//      - parent page can resize iframe safely
//
// IMPORTANT
//  • This file does NOT change form schema.
//  • This file does NOT change publishing logic.
//  • This file does NOT change /forms/:formId.
//  • This file does NOT change FormBuilderWrapper.
//  • This file is inert until mounted by the /embed/forms/:formId route.
//
// EVENT CONTRACT
//  The embedded app sends postMessage events shaped like:
//
//    {
//      source: "utm-smart-manager-embed",
//      version: 1,
//      type: "embed_ready" | "height_change" | "form_start" |
//            "form_submit_attempt" | "form_submit_success" |
//            "form_submit_error" | "premium_feature_locked",
//      formId,
//      route,
//      timestamp,
//      payload
//    }
//
// SECURITY NOTE
//  • In production, prefer an explicit allowed parent origin list.
//  • In local/dev mode, localhost origins are allowed for testing.
//  • If no trusted parent origin is detected, messages are not sent.
// -----------------------------------------------------------------------------

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useLocation, useParams } from "react-router-dom";

// Keep the source stable so the microsite can filter messages safely.
export const EMBED_MESSAGE_SOURCE = "utm-smart-manager-embed";
export const EMBED_MESSAGE_VERSION = 1;

// Centralized event names so the app runtime and parent microsite do not drift.
// Parent pages should filter by EMBED_MESSAGE_SOURCE, then switch on `type`.
export const EMBED_EVENT_TYPES = Object.freeze({
  EMBED_READY: "embed_ready",
  FORM_VIEW: "form_view",
  HEIGHT_CHANGE: "height_change",
  FORM_START: "form_start",
  FORM_SUBMIT_ATTEMPT: "form_submit_attempt",
  FORM_SUBMIT_SUCCESS: "form_submit_success",
  FORM_SUBMIT_ERROR: "form_submit_error",
  PREMIUM_FEATURE_LOCKED: "premium_feature_locked",
});

// Avoid postMessage height spam from tiny sub-pixel layout changes.
export const EMBED_HEIGHT_DELTA_THRESHOLD_PX = 4;

// Development + first microsite POC allowlist.
// You can later move this to an environment variable or org/form-level setting.
const DEFAULT_ALLOWED_PARENT_ORIGINS = [
  "http://localhost:3000",
  "http://localhost:3001",
  "http://localhost:5173",
  "https://adobe-utm-builder.com",
  "https://www.adobe-utm-builder.com",
];

const EmbedRuntimeContext = createContext({
  isEmbedRoute: false,
  formId: null,
  parentOrigin: null,
  allowedParentOrigins: DEFAULT_ALLOWED_PARENT_ORIGINS,
  canMessageParent: false,
  emitEmbedEvent: () => {},
});

const normalizeOrigin = (value) => {
  if (!value || typeof value !== "string") return null;

  try {
    return new URL(value).origin;
  } catch (_) {
    return null;
  }
};

const getParentOriginFromDocumentReferrer = () => {
  if (typeof document === "undefined") return null;
  return normalizeOrigin(document.referrer);
};

const getAllowedOriginsFromEnv = () => {
  try {
    const raw = process.env.REACT_APP_EMBED_ALLOWED_PARENT_ORIGINS;
    if (!raw || typeof raw !== "string") {
      return DEFAULT_ALLOWED_PARENT_ORIGINS;
    }

    const parsed = raw
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);

    return parsed.length > 0 ? parsed : DEFAULT_ALLOWED_PARENT_ORIGINS;
  } catch (_) {
    return DEFAULT_ALLOWED_PARENT_ORIGINS;
  }
};

const isAllowedParentOrigin = (origin, allowedOrigins) => {
  if (!origin) return false;
  if (!Array.isArray(allowedOrigins)) return false;

  return allowedOrigins.includes(origin);
};

export function EmbedRuntimeProvider({
  children,

  // Optional overrides for future enterprise settings.
  // For the microsite POC, defaults are enough.
  allowedParentOrigins: allowedParentOriginsProp = null,
}) {
  const location = useLocation();
  const params = useParams();

  const routeFormId = params?.formId || null;

  const isEmbedRoute = useMemo(() => {
    return location.pathname.startsWith("/embed/forms/");
  }, [location.pathname]);

  const allowedParentOrigins = useMemo(() => {
    return Array.isArray(allowedParentOriginsProp) &&
      allowedParentOriginsProp.length > 0
      ? allowedParentOriginsProp
      : getAllowedOriginsFromEnv();
  }, [allowedParentOriginsProp]);

  const parentOrigin = useMemo(() => {
    const referrerOrigin = getParentOriginFromDocumentReferrer();

    if (isAllowedParentOrigin(referrerOrigin, allowedParentOrigins)) {
      return referrerOrigin;
    }

    return null;
  }, [allowedParentOrigins]);

  const canMessageParent = Boolean(
    isEmbedRoute &&
      parentOrigin &&
      typeof window !== "undefined" &&
      window.parent &&
      window.parent !== window
  );

  const emitEmbedEvent = useCallback(
    (type, payload = {}) => {
      if (!isEmbedRoute) return;

      const isInsideIframe =
        typeof window !== "undefined" &&
        window.parent &&
        window.parent !== window;

      // Direct-route testing is valid during development:
      //   /embed/forms/:formId
      //
      // In that case there is no iframe parent, so there is nothing to message.
      // This should be silent because it is not an error and not an embed failure.
      if (!isInsideIframe) {
        if (process.env.NODE_ENV !== "production") {
          console.debug("[EmbedRuntime] Direct embed route test; postMessage skipped.", {
            type,
            route: location.pathname,
          });
        }
        return;
      }

      // If we ARE inside an iframe but parentOrigin is missing or not allowlisted,
      // keep the warning because this is an actual embed configuration issue.
      if (!canMessageParent) {
        console.warn("[EmbedRuntime] Skipping postMessage; no allowed parent origin.", {
          type,
          route: location.pathname,
          parentOrigin,
          allowedParentOrigins,
        });
        return;
      }

      const message = {
        source: EMBED_MESSAGE_SOURCE,
        version: EMBED_MESSAGE_VERSION,
        type,
        formId: routeFormId,
        route: location.pathname,
        timestamp: new Date().toISOString(),
        payload: payload && typeof payload === "object" ? payload : {},
      };

      try {
        window.parent.postMessage(message, parentOrigin);

        if (process.env.NODE_ENV !== "production") {
          console.log("[EmbedRuntime] postMessage sent:", message);
        }
      } catch (error) {
        console.error("[EmbedRuntime] Failed to postMessage:", {
          type,
          parentOrigin,
          error,
        });
      }
    },
    [
      isEmbedRoute,
      canMessageParent,
      location.pathname,
      parentOrigin,
      allowedParentOrigins,
      routeFormId,
    ]
  );

  const value = useMemo(
    () => ({
      isEmbedRoute,
      formId: routeFormId,
      parentOrigin,
      allowedParentOrigins,
      canMessageParent,
      emitEmbedEvent,
    }),
    [
      isEmbedRoute,
      routeFormId,
      parentOrigin,
      allowedParentOrigins,
      canMessageParent,
      emitEmbedEvent,
    ]
  );

  useEffect(() => {
    if (!isEmbedRoute) return;

    console.log("[EmbedRuntime] Initialized embed runtime:", {
      formId: routeFormId,
      route: location.pathname,
      parentOrigin,
      canMessageParent,
      allowedParentOrigins,
    });
  }, [
    isEmbedRoute,
    routeFormId,
    location.pathname,
    parentOrigin,
    canMessageParent,
    allowedParentOrigins,
  ]);

  return (
    <EmbedRuntimeContext.Provider value={value}>
      {children}
    </EmbedRuntimeContext.Provider>
  );
}

export const useEmbedRuntime = () => useContext(EmbedRuntimeContext);

// -----------------------------------------------------------------------------
// Height reporter hook
//
// Use this inside EmbeddedPublishedFormPage after the root container exists.
// It observes the embedded document height and notifies the parent page.
//
// This is intentionally exported from the same file so iframe behavior remains
// centralized and does not spread across the form renderer.
// -----------------------------------------------------------------------------
export function useEmbedHeightReporter(rootRef, dependencies = []) {
  const { isEmbedRoute, emitEmbedEvent } = useEmbedRuntime();

  const lastReportedHeightRef = useRef(0);

  const reportHeight = useCallback(() => {
    if (!isEmbedRoute) return;

    try {
      const root = rootRef?.current || document.documentElement;
      const body = document.body;
      const html = document.documentElement;

      const measuredHeight = Math.max(
        root?.scrollHeight || 0,
        root?.offsetHeight || 0,
        body?.scrollHeight || 0,
        body?.offsetHeight || 0,
        html?.clientHeight || 0,
        html?.scrollHeight || 0,
        html?.offsetHeight || 0
      );

      const nextHeight = Math.ceil(measuredHeight);

      if (!Number.isFinite(nextHeight) || nextHeight <= 0) {
        return;
      }

      const previousHeight = lastReportedHeightRef.current;

      // Enterprise iframe behavior:
      // Skip tiny changes so the parent page does not receive resize noise from
      // sub-pixel layout changes, font settling, focus rings, or MUI micro-layout.
      if (
        previousHeight > 0 &&
        Math.abs(previousHeight - nextHeight) < EMBED_HEIGHT_DELTA_THRESHOLD_PX
      ) {
        return;
      }

      lastReportedHeightRef.current = nextHeight;

      emitEmbedEvent(EMBED_EVENT_TYPES.HEIGHT_CHANGE, {
        height: nextHeight,
      });
    } catch (error) {
      console.error("[EmbedRuntime] Failed to report iframe height:", error);
    }
  }, [isEmbedRoute, emitEmbedEvent, rootRef]);

  useEffect(() => {
    if (!isEmbedRoute) return;

    let rafId = null;

    const scheduleReport = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }

      rafId = requestAnimationFrame(() => {
        reportHeight();
      });
    };

    scheduleReport();

    const root = rootRef?.current || document.body;

    let observer = null;

    try {
      if (typeof ResizeObserver !== "undefined" && root) {
        observer = new ResizeObserver(scheduleReport);
        observer.observe(root);
      }
    } catch (error) {
      console.warn("[EmbedRuntime] ResizeObserver unavailable or failed:", error);
    }

    window.addEventListener("load", scheduleReport);
    window.addEventListener("resize", scheduleReport);

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }

      if (observer) {
        try {
          observer.disconnect();
        } catch (_) {}
      }

      window.removeEventListener("load", scheduleReport);
      window.removeEventListener("resize", scheduleReport);
    };

    // dependencies intentionally supplied by caller so form output changes,
    // validation changes, table/card changes, etc. can trigger remeasurement.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmbedRoute, reportHeight, rootRef, ...dependencies]);
}

export default EmbedRuntimeContext;